// Input: year (number), month (1-12), day (1-31)
// Output: UTC timestamp (ms) for that date
export function getDay(year, month, day) {
  month = month - 1;
  const date = new Date(Date.UTC(year, month, day));
  const timeStamp = date.getTime();
  return timeStamp;
}

// Input: UNIX timestamp (seconds)
// Output: string like "maandag 12 december"
export function getReadableDay(timestamp, includeYear = false) {
  const date = new Date(timestamp * 1000);
  const day = date.toLocaleDateString('nl-NL', { weekday: 'long' });
  const month = date.toLocaleDateString('nl-NL', { month: 'long' });
  const dayNumber = date.toLocaleDateString('nl-NL', { day: 'numeric' });
  if (includeYear) {
    const year = date.toLocaleDateString('nl-NL', { year: 'numeric' });
    return `${day} ${dayNumber} ${month} ${year}`;
  }
  return `${day} ${dayNumber} ${month}`;
}

// Input: ISO date string (e.g. 2024-01-05T14:00:00Z)
// Output: "5 januari 2024 15:00" in nl-NL locale
export function getDirectusDate(isoDate) {
  const date = new Date(isoDate);
  const formatted = date.toLocaleString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return formatted;
}

// Input: date (Date|string|number), toFormat one of DMY|YMD|MDY
// Output: formatted string, defaults to dd-mm-yyyy
export function transformDate(date, toFormat = 'DMY') {
  if (!date) return '';
  const d = new Date(date);
  const day = `0${d.getDate()}`.slice(-2);
  const month = `0${d.getMonth() + 1}`.slice(-2);
  const year = d.getFullYear();
  if (toFormat === 'DMY') {
    return `${day}-${month}-${year}`;
  } else if (toFormat === 'YMD') {
    return `${year}-${month}-${day}`;
  } else if (toFormat === 'MDY') {
    return `${month}-${day}-${year}`;
  }
  return date;
}

// Input: Date|string|number
// Output: "12 december 2025" (nl-NL) or null when invalid
export function formatDate(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

// Input: Date|string|number
// Output: "12 december 2025 14:30" (nl-NL) or null when invalid
export function formatDateTime(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Weeknummers in deze app zijn ISO 8601-weken: week 1 is de week waarin 4 januari valt
 * (de eerste donderdag), weken lopen van maandag t/m zondag, en een jaar heeft 52 óf 53
 * weken (2020, 2026, 2032, ...). Dagen rond de jaarwisseling horen daardoor soms bij het
 * weekjaar ernaast: 29 dec 2025 is week 1 van 2026, 1 jan 2027 is week 53 van 2026.
 * Gebruik voor navigatie altijd `shiftWeek` en voor het aantal weken `getWeeksInYear`;
 * nooit een hardcoded 52.
 */

// Input: year (number)
// Output: 52 of 53 — het aantal ISO-weken in dat weekjaar
export function getWeeksInYear(year) {
  // 28 december ligt per definitie altijd in de laatste ISO-week van zijn jaar.
  const lastWeekDay = new Date(year, 11, 28, 12);
  return getWeekNumberForTimestamp(Math.floor(lastWeekDay.getTime() / 1000)).weekNumber;
}

// Input: weekNumber (1-53, ISO), optional year (ISO-weekjaar). Zonder weekNumber: de huidige week.
// Output: { weekNumber, year, startOfWeek: unixSeconds, endOfWeek: unixSeconds }
// startOfWeek is maandag 00:00 lokale tijd, endOfWeek de maandag daarna 00:00 lokale tijd.
export function getWeek(weekNumber, year) {
  if (!weekNumber) {
    // De lopende week; het ISO-weekjaar kan hier afwijken van het kalenderjaar (29-31 dec, 1-3 jan).
    const current = getWeekNumberForTimestamp(Math.floor(Date.now() / 1000));
    weekNumber = current.weekNumber;
    year = current.year;
  }
  if (!year) {
    year = new Date().getFullYear();
  }

  const startDate = getMondayOfWeek(weekNumber, year);
  startDate.setHours(0, 0, 0, 0);
  // Via setDate i.p.v. +7×86400s, zodat een zomertijdwissel binnen de week de grens niet verschuift.
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 7);

  return {
    weekNumber,
    year,
    startOfWeek: Math.floor(startDate.getTime() / 1000),
    endOfWeek: Math.floor(endDate.getTime() / 1000),
  };
}

// Input: weekNumber + year (ISO), delta in weken (bv. +1 of -1)
// Output: zelfde vorm als getWeek(), voor de week `delta` weken verderop — over jaargrenzen
// heen, inclusief week 53 waar die bestaat.
export function shiftWeek(weekNumber, year, delta) {
  const monday = getMondayOfWeek(weekNumber, year);
  // Donderdag 12:00 van de doelweek: ligt altijd in het juiste ISO-weekjaar en blijft buiten zomertijd-randen.
  const thursday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + delta * 7 + 3, 12);
  const target = getWeekNumberForTimestamp(Math.floor(thursday.getTime() / 1000));
  return getWeek(target.weekNumber, target.year);
}

/**
 * Return the monday of the given week number.
 * @param {*} weekNumber
 * @param {*} year
 * @returns
 */
// Input: ISO week number + year
// Output: Date object representing Monday of that week (local midnight)
export function getMondayOfWeek(weekNumber, year) {
  const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
  const dayOfWeek = simple.getDay();
  const isoWeekStart = simple;
  if (dayOfWeek <= 4) {
    isoWeekStart.setDate(simple.getDate() - simple.getDay() + 1); // maandag is dag 1
  } else {
    isoWeekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  return isoWeekStart;
}

/**
 * Get week days with timestamps
 * @param {*} weekNumber
 * @param {*} year
 * @returns
 */
export function getWeekDaysWithTimestamps(weekNumber, year, maxWeekDays = 7) {
  var weekdays = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
  var months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

  // Bereken het huidige jaar als het niet wordt meegegeven
  if (!year) {
    year = new Date().getFullYear();
  }
  // Haal de maandag van de gegeven week op
  var startOfWeek = getMondayOfWeek(weekNumber, year);

  var weekDaysWithMidnightTimestamps = [];
  const daysInWeek = maxWeekDays + 1;
  // Loop from monday to saturday
  for (var i = 1; i < daysInWeek; i++) {
    var currentDate = new Date(startOfWeek);
    currentDate.setDate(startOfWeek.getDate() + i);

    // Set time to midnight (00:00:00) for equal comparison
    // currentDate.setHours(0, 0, 0, 0);
    // Set the time to midnight
    currentDate.setUTCHours(0);
    currentDate.setUTCMinutes(0);
    currentDate.setUTCSeconds(0);
    currentDate.setUTCMilliseconds(0);

    var day = weekdays[currentDate.getDay()];
    var date = currentDate.getDate();
    var month = months[currentDate.getMonth()];
    var formattedDate = `${day} ${date} ${month}`;
    var timestamp = Math.floor(currentDate.getTime() / 1000);

    weekDaysWithMidnightTimestamps.push({
      date: formattedDate,
      timestamp: timestamp,
    });
  }

  return weekDaysWithMidnightTimestamps;
}

/**
 * getWeekNumberForTimestamp()
 */
// Input: UNIX timestamp (seconds)
// Output: { weekNumber, year } ISO week info
export function getWeekNumberForTimestamp(timestamp) {
  const date = new Date(timestamp * 1000);

  // Donderdag in deze week pakken
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));

  // Eerste donderdag van het jaar vinden
  const firstThursday = new Date(date.getFullYear(), 0, 4);
  firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));

  // Aantal weken tellen
  const weekNumber = 1 + Math.round((date - firstThursday) / (7 * 24 * 3600 * 1000));

  return { weekNumber, year: date.getFullYear() };
}

/**
 * Get amount of working days in a week
 * @param {*} workingDaysConfig (array)
 * @return number
 */
// Input: JSON string or array of working days
// Output: number of enabled working days
export function getWorkingDaysInWeek(workingDaysConfig) {
  // TODO make it so that we actually disable a specific day. Currently we just count the days, but when Saturday is diabled and Sunday is enabled, we still count Saturday.
  if (typeof workingDaysConfig === 'string') {
    workingDaysConfig = JSON.parse(workingDaysConfig);
  }
  if (!workingDaysConfig) {
    return 6;
  } else {
    const count = workingDaysConfig.length;
    return count;
  }
}
